import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from '../map/MapContext';
import { buildXyzTileUrl } from '../config/environment';
import {
  getDatasetLayers,
  listDatasets,
  refreshDatasetCatalog,
  uploadDataset,
  type DatasetLayer,
  type DatasetSummary,
} from '../services/DatasetService';

function isSupportedArchive(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.gpkg') || name.endsWith('.mbtiles');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function isRasterTileLayer(layer: DatasetLayer): boolean {
  return layer.kind === 'raster-tiles' || layer.kind === 'tiles';
}

function layerTypeLabel(layer: DatasetLayer): string {
  if (isRasterTileLayer(layer)) return 'Raster chart';
  if (layer.kind === 'vector-tiles') return 'Vector tiles';
  if (layer.kind === 'features') return 'Feature collection';
  return 'Attributes';
}

function catalogRasterId(dataset: DatasetSummary, layer: DatasetLayer): string {
  return `catalog-raster:${dataset.id}:${layer.name}`;
}

export const DatasetUploadPanel: React.FC = () => {
  const { map } = useMap();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [expandedDatasetId, setExpandedDatasetId] = useState<string | null>(null);
  const [visibleRasterLayers, setVisibleRasterLayers] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string>('Drop GeoPackage or MBTiles archives here.');
  const [error, setError] = useState<string | null>(null);

  const loadDatasets = useCallback(async () => {
    try {
      const next = await listDatasets(true);
      setDatasets(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load datasets');
    }
  }, []);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const selected = Array.from(files).filter(isSupportedArchive);
    const skipped = Array.from(files).length - selected.length;
    if (selected.length === 0) {
      setError('Choose .gpkg or .mbtiles files.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      for (const file of selected) {
        setStatus(`Uploading ${file.name}...`);
        const result = await uploadDataset(file, overwrite);
        setStatus(`Uploaded ${result.uploaded.file} (${formatBytes(result.uploaded.bytes)}). Catalog has ${result.count} datasets.`);
      }
      if (skipped > 0) {
        setStatus((prev) => `${prev} Skipped ${skipped} unsupported file(s).`);
      }
      await loadDatasets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dataset upload failed');
    } finally {
      setBusy(false);
    }
  }, [loadDatasets, overwrite]);

  const refresh = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await refreshDatasetCatalog();
      setStatus(`Catalog refreshed. Found ${result.count} datasets.`);
      await loadDatasets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Catalog refresh failed');
    } finally {
      setBusy(false);
    }
  }, [loadDatasets]);

  const toggleRasterLayer = useCallback((dataset: DatasetSummary, layer: DatasetLayer) => {
    if (!map) return;

    const layerId = catalogRasterId(dataset, layer);
    const sourceId = `${layerId}:source`;
    const isVisible = map.getLayer(layerId) && map.getLayoutProperty(layerId, 'visibility') !== 'none';
    const nextVisible = !isVisible;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'raster',
        tiles: [buildXyzTileUrl(dataset.id, layer.name)],
        tileSize: 256,
      });
    }
    if (!map.getLayer(layerId)) {
      const firstVectorLayer = map.getStyle().layers?.find((item) => item.type !== 'background' && item.type !== 'raster');
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 0.85 },
      }, firstVectorLayer?.id);
    }
    map.setLayoutProperty(layerId, 'visibility', nextVisible ? 'visible' : 'none');
    setVisibleRasterLayers((current) => {
      const next = new Set(current);
      if (nextVisible) next.add(layerId);
      else next.delete(layerId);
      return next;
    });
  }, [map]);

  return (
    <div className="panel-section">
      <div className="panel-section-title">Dataset Catalog</div>
      <div
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `1px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
          background: dragging ? 'rgba(59, 130, 246, 0.12)' : 'rgba(15, 23, 42, 0.35)',
          borderRadius: 6,
          padding: 12,
          cursor: busy ? 'wait' : 'pointer',
          color: 'var(--text-secondary)',
          fontSize: 12,
          textAlign: 'center',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".gpkg,.mbtiles"
          multiple
          disabled={busy}
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.currentTarget.value = '';
          }}
        />
        <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Drop .gpkg / .mbtiles files
        </div>
        <div>{busy ? 'Working...' : status}</div>
      </div>

      <label className="toggle" style={{ display: 'flex', marginTop: 8 }}>
        <span
          className={`toggle-box ${overwrite ? 'checked' : ''}`}
          onClick={() => setOverwrite((prev) => !prev)}
        />
        <span style={{ fontSize: 12 }}>Overwrite files with the same name</span>
      </label>

      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button className="btn btn-secondary" onClick={refresh} disabled={busy} style={{ flex: 1 }}>
          Refresh Catalog
        </button>
        <button className="btn btn-secondary" onClick={loadDatasets} disabled={busy} style={{ flex: 1 }}>
          Reload List
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 8, color: 'var(--danger, #EF4444)', fontSize: 11 }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12 }}>
        <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>
          Available datasets: {datasets.length}
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {datasets.map((dataset) => {
            const isExpanded = expandedDatasetId === dataset.id;
            const layers = getDatasetLayers(dataset);
            return (
              <div key={dataset.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.4)' }}>
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  className={`list-item ${isExpanded ? 'active' : ''}`}
                  onClick={() => setExpandedDatasetId(isExpanded ? null : dataset.id)}
                  style={{ width: '100%', border: 0, color: 'inherit', textAlign: 'left', background: isExpanded ? 'rgba(59,130,246,0.15)' : 'transparent' }}
                >
                  <span aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', overflowWrap: 'anywhere', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{dataset.id}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>{layers.length} layer{layers.length === 1 ? '' : 's'}</span>
                  </span>
                </button>
                {isExpanded && (
                  <div style={{ padding: '4px 4px 8px 20px', background: 'rgba(15,23,42,0.2)' }}>
                    {layers.map((layer) => {
                      const layerId = catalogRasterId(dataset, layer);
                      const visible = visibleRasterLayers.has(layerId)
                        || Boolean(map?.getLayer(layerId) && map.getLayoutProperty(layerId, 'visibility') !== 'none');
                      return (
                        <div key={layer.name} className="list-item" style={{ minHeight: 34, padding: '5px 4px', cursor: 'default' }}>
                          {isRasterTileLayer(layer) && (
                            <button
                              type="button"
                              className="btn-icon"
                              aria-label={`${visible ? 'Hide' : 'Show'} ${layer.name}`}
                              title={`${visible ? 'Hide' : 'Show'} ${layer.name}`}
                              onClick={() => toggleRasterLayer(dataset, layer)}
                              style={{ width: 22, height: 22 }}
                            >
                              <span className={`toggle-box ${visible ? 'checked' : ''}`} style={{ width: 16, height: 16 }} />
                            </button>
                          )}
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'block', overflowWrap: 'anywhere', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{layer.name}</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>{layerTypeLabel(layer)}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
